/**
 * Hook to capture the PWA beforeinstallprompt event and manage install state.
 */

import { useState, useEffect, useCallback } from 'react';

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
  prompt(): Promise<void>;
}

interface InstallPromptState {
  /** Whether the install prompt is available (browser supports it and app is not installed) */
  canInstall: boolean;
  /** Whether the app is already installed as PWA */
  isInstalled: boolean;
  /** Trigger the native install prompt */
  promptInstall: () => Promise<boolean>;
  /** Dismiss the install banner (user chose "not now") */
  dismiss: () => void;
  /** Whether the user has dismissed the banner in this session */
  isDismissed: boolean;
}

const DISMISS_KEY = 'frameit-pwa-dismiss';
const DISMISS_DURATION_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

function wasDismissedRecently(): boolean {
  const ts = localStorage.getItem(DISMISS_KEY);
  if (!ts) return false;
  return Date.now() - Number(ts) < DISMISS_DURATION_MS;
}

export function useInstallPrompt(): InstallPromptState {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isDismissed, setIsDismissed] = useState(wasDismissedRecently);

  useEffect(() => {
    // Check if already installed via display-mode
    const mq = window.matchMedia('(display-mode: standalone)');
    if (mq.matches || (navigator as unknown as { standalone?: boolean }).standalone) {
      setIsInstalled(true);
      return;
    }

    const handleBeforeInstall = (e: Event): void => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    const handleAppInstalled = (): void => {
      setIsInstalled(true);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    window.addEventListener('appinstalled', handleAppInstalled);

    return (): void => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const promptInstall = useCallback(async (): Promise<boolean> => {
    if (!deferredPrompt) return false;
    await deferredPrompt.prompt();
    const result = await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    return result.outcome === 'accepted';
  }, [deferredPrompt]);

  const dismiss = useCallback((): void => {
    setIsDismissed(true);
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  }, []);

  return {
    canInstall: deferredPrompt !== null && !isInstalled && !isDismissed,
    isInstalled,
    promptInstall,
    dismiss,
    isDismissed,
  };
}
